import { useState, useCallback, useRef } from 'react';

export interface LTMatch {
  message: string;
  offset: number;
  length: number;
  replacements: { value: string }[];
  rule: { id: string; description: string; category: { name: string } };
  context: { text: string; offset: number; length: number };
}

interface UseLanguageToolReturn {
  check: (text: string) => Promise<LTMatch[]>;
  matches: LTMatch[];
  isChecking: boolean;
  error: string | null;
  clear: () => void;
}

const LT_API = 'https://api.languagetool.org/v2/check';

export function useLanguageTool(language = 'en-US'): UseLanguageToolReturn {
  const [matches, setMatches] = useState<LTMatch[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const check = useCallback(
    async (text: string): Promise<LTMatch[]> => {
      if (!text.trim()) {
        setMatches([]);
        return [];
      }

      // Cancel previous in-flight request
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setIsChecking(true);
      setError(null);

      try {
        const body = new URLSearchParams({
          text,
          language,
          enabledOnly: 'false',
        });

        const res = await fetch(LT_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
          signal: abortRef.current.signal,
        });

        if (!res.ok) throw new Error(`LanguageTool error: ${res.status}`);

        const data = await res.json();
        const found: LTMatch[] = data.matches ?? [];
        setMatches(found);
        return found;
      } catch (err: any) {
        if (err.name === 'AbortError') return [];
        const msg = err.message || 'Grammar check failed';
        setError(msg);
        return [];
      } finally {
        setIsChecking(false);
      }
    },
    [language]
  );

  const clear = useCallback(() => {
    setMatches([]);
    setError(null);
  }, []);

  return { check, matches, isChecking, error, clear };
}
